import { redirect } from "react-router";
import { toast } from "sonner";
import useUserState from "~/stores/user";
import type { SoPermission } from "~/types/ui";
import { clearSession } from "./session-util";

// If the prefix is undefined it will default to "Frontend.SO." in the isPermitted function
export function pageGuard(key: SoPermission, prefix?: string) {
  const { isPermitted, isLoggedIn } = useUserState.getState();
  if (!isLoggedIn()) return redirect("/login");
  if (!isPermitted(key, prefix)) return redirect("/");

  return {};
}

/**
 * Guard for the root route, which `pageGuard` cannot serve: it answers a missing
 * permission with a redirect to "/", and from "/" that is a loop.
 *
 * A user who may not see the home page has nowhere left to be sent, so the session ends
 * instead. That also unties every other route — they redirect to "/", which now lands on
 * the login screen rather than bouncing forever.
 */
export function rootGuard(key: SoPermission, prefix?: string) {
  const { isPermitted, isLoggedIn, hasTokenExpired, sso } =
    useUserState.getState();
  if (!isLoggedIn()) return redirect("/login");

  if (!sso && hasTokenExpired()) {
    toast.info("Session Expired");
    clearSession();
    return redirect("/login");
  }

  if (!isPermitted(key, prefix)) {
    clearSession();
    return redirect("/login");
  }

  return {};
}

// For routes that already have a clientLoader of their own: throws the redirect rather
// than returning it, so the guard is one statement at the top of the loader instead of a
// branch around the rest of it.
export function requirePermission(key: SoPermission, prefix?: string) {
  const guard = pageGuard(key, prefix);
  if (guard instanceof Response) throw guard;
}

// If the prefix is undefined it will default to "Frontend.SO." in the isPermitted function
export function permitted(key: SoPermission, prefix?: string): boolean {
  const { isPermitted, isLoggedIn } = useUserState.getState();
  return isLoggedIn() && isPermitted(key, prefix);
}

type JwtResult = {
  exp: number;
  iat: number;
};

export function parseJwt(token: string): JwtResult {
  var base64Url = token.split(".")[1];
  var base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  var jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(""),
  );

  return JSON.parse(jsonPayload) as JwtResult;
}
